import { RadioGroup } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../App/redux/store';
import { adActions, getAdState } from '../../../../adSlice';
import styles from './similarDpeResult.module.scss';
import SimilarDpeResultCard from './SimilarDpeResultCard';

interface ISimilarDpesResultContainerProps {
  adCity: ICity;
}

const ITEMS_PER_PAGE = 5;

function SimilarDpesResultContainer({ adCity }: ISimilarDpesResultContainerProps) {
  const {
    similarAdDpes,
    adGeoAddress,
    highlightedDpeId,
    highlightedDpeIdIsScrolling,
  } = useAppSelector(getAdState);
  const dispatch = useAppDispatch();

  const [currentPage, setCurrentPage] = useState(1);
  const [lastProcessedHighlightId, setLastProcessedHighlightId] = useState<
    string | null
  >(null);

  const handleRadioClick = (address: string) => {
    dispatch(adActions.adGeoAddressToggle(address));
  };

  // Group DPE entries by unique address
  const groupedDpes =
    similarAdDpes.result?.reduce<MergedAdDpe[]>(
      (groups, { numDpe, dateDpe, dpe, ges, landArea, ...dpeData }) => {
        const existingGroup = groups.find(
          (group) => group.uniqueDpeId === dpeData.uniqueDpeId,
        );

        if (existingGroup) {
          existingGroup.dpeEntries.push({
            numDpe,
            dateDpe,
            dpe,
            ges,
            surface: landArea,
          });
        } else {
          groups.push({
            ...dpeData,
            landArea,
            dpe,
            ges,
            dpeEntries: [
              {
                numDpe,
                dateDpe,
                dpe,
                ges,
                surface: landArea,
              },
            ],
          });
        }

        return groups;
      },
      [],
    ) || [];

  // Pagination calculations
  const totalPages = Math.ceil(groupedDpes.length / ITEMS_PER_PAGE);
  const pageStartIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const pageEndIndex = pageStartIndex + ITEMS_PER_PAGE;
  const currentPageItems = groupedDpes.slice(pageStartIndex, pageEndIndex);

  const getPageForDpeId = (dpeId: string): number => {
    const dpeIndex = groupedDpes.findIndex((dpe) => dpe.uniqueDpeId === dpeId);
    return dpeIndex !== -1 ? Math.floor(dpeIndex / ITEMS_PER_PAGE) + 1 : 1;
  };

  const scrollToElement = (elementId: string) => {
    const tryScroll = () => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
  };

  // Reset pagination to page 1 when new search results arrive
  useEffect(() => {
    setCurrentPage(1);
  }, [similarAdDpes.result]);

  // Navigate to page containing highlighted DPE
  useEffect(() => {
    if (
      !highlightedDpeId ||
      highlightedDpeId === lastProcessedHighlightId ||
      groupedDpes.length === 0
    ) {
      return;
    }

    const targetPage = getPageForDpeId(highlightedDpeId);
    setCurrentPage(targetPage);
    setLastProcessedHighlightId(highlightedDpeId);
  }, [highlightedDpeId, groupedDpes, lastProcessedHighlightId]);

  // Scroll to highlighted item when page changes
  useEffect(() => {
    if (highlightedDpeId && highlightedDpeIdIsScrolling) {
      scrollToElement(highlightedDpeId);
      dispatch(adActions.highlightedDpeIdIsScrollingSet(false));
    }
  }, [highlightedDpeId, currentPage, highlightedDpeIdIsScrolling]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);

    // Prevent auto-navigation back to highlighted page on manual page change
    if (highlightedDpeId) {
      setLastProcessedHighlightId(highlightedDpeId);
    }
  };

  if (!similarAdDpes.result) return null;

  return (
    <section className={styles.similarDpeResultContainer}>
      <h3>{`Bien(s) DPE identifié(s) possédant les mêmes caractéristiques à ${adCity?.name}`}</h3>

      <div className={styles.cardContainer}>
        {groupedDpes.length === 0 ? (
          <div className={styles.emptyState}>
            <p>Aucune annonce trouvée avec les critères DPE spécifiés.</p>
          </div>
        ) : (
          <>
            <RadioGroup value={adGeoAddress}>
              {currentPageItems.map((dpe) => (
                <SimilarDpeResultCard
                  key={dpe.uniqueDpeId}
                  mergedAdDpe={dpe}
                  isHighlighted={highlightedDpeId === dpe.uniqueDpeId}
                  onRadioClick={handleRadioClick}
                />
              ))}
            </RadioGroup>

            {totalPages > 1 && (
              <div className={styles.paginationContainer}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  variant="outlined"
                  color="primary"
                  showFirstButton
                  showLastButton
                />
                <div className={styles.paginationInfo}>
                  Affichage {pageStartIndex + 1} à{' '}
                  {Math.min(pageEndIndex, groupedDpes.length)} sur{' '}
                  {groupedDpes.length} résultats
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default SimilarDpesResultContainer;
